import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';

const AudioPlayer = forwardRef(({
  audioUrl,
  audioLoading,
  audioTimestamps,
  onTimeUpdate,
  onPlay,
  onPause,
  onEnded,
  onLoadedData,
  currentPage
}, ref) => {
  const audioRef = useRef(null);

  // Expose the audio element to the parent via ref
  useImperativeHandle(ref, () => ({
    get audio() { return audioRef.current; },
    play() { return audioRef.current?.play(); },
    pause() { audioRef.current?.pause(); },
    get currentTime() { return audioRef.current?.currentTime ?? 0; },
    set currentTime(t) { if (audioRef.current) audioRef.current.currentTime = t; },
    get playbackRate() { return audioRef.current?.playbackRate ?? 1; },
    set playbackRate(r) { if (audioRef.current) audioRef.current.playbackRate = r; },
    get paused() { return audioRef.current?.paused ?? true; },
    get duration() { return audioRef.current?.duration ?? 0; },
  }));

  // Pass the audio ref to parent component for time tracking
  useEffect(() => {
    if (audioRef.current && onTimeUpdate) {
      const handleTimeUpdate = () => {
        if (audioRef.current) {
          onTimeUpdate(audioRef.current);
        }
      };

      const handlePlay = () => {
        if (onPlay) onPlay();
      };

      const handlePause = () => {
        if (onPause) onPause();
      };

      const handleEnded = () => {
        if (onEnded) onEnded();
      };

      const handleLoadedData = () => {
        if (onLoadedData) onLoadedData();
      };

      const handleError = (e) => {
        console.error('Audio error:', e);
      };

      const audio = audioRef.current;
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('error', handleError);

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('loadeddata', handleLoadedData);
        audio.removeEventListener('error', handleError);
      };
    }
  }, [audioUrl, audioTimestamps, onTimeUpdate, onPlay, onPause, onEnded, onLoadedData]);

  if (audioLoading) {
    return (
      <div className="mt-4 flex items-center justify-center bg-gray-100 rounded-full py-2 px-4">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900 mr-2"></div>
        <span className="text-gray-700 font-medium">Creating audio...</span>
      </div>
    );
  }

  if (audioUrl) {
    return (
      <div className="mt-4 flex items-center bg-gray-50 rounded-xl py-3 px-4 shadow-sm border border-gray-200">
        <div className="mr-3 bg-gray-900 rounded-full p-2 shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414-7.072m-2.829 9.9a9 9 0 010-12.728" />
          </svg>
        </div>
        <div className="flex-grow">
          <div className="font-medium text-sm text-gray-800 mb-1">
            Listen to the story
          </div>
          <audio
            ref={audioRef}
            controls
            src={audioUrl}
            className="w-full h-8"
            preload="auto"
          />
        </div>
      </div>
    );
  }

  return null;
});

AudioPlayer.displayName = 'AudioPlayer';

AudioPlayer.propTypes = {
  audioUrl: PropTypes.string,
  audioLoading: PropTypes.bool,
  audioTimestamps: PropTypes.object,
  onTimeUpdate: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnded: PropTypes.func,
  onLoadedData: PropTypes.func,
  currentPage: PropTypes.object
};

export default AudioPlayer;
